import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';
import { toast } from '../../toasts';
import { MdAddchart } from 'react-icons/md';
import { getError, showCurrency } from '../../utils';
import { EntityList, PageHead, DataTable, saveBlob } from '../../components/EntityList';
import Paginator from '../Paginator/Paginator';
import PredictionTypeCards from './PredictionTypeCards';
import ProjectionPanel from './ProjectionPanel';
import PredictionFilterPanel from './PredictionFilterPanel';
import { getPredictionType } from './predictionTypes';
import { buildProjection } from './predictionProjection';
import { generatePredictionFile } from './predictionFileBuilder';

moment.locale('es');

const DEFAULT_FILTERS = { horizonte: '6', moneda: 'all', dateFrom: '', dateTo: '' };
const DETAIL_PAGE_SIZE = 15;

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, rows: action.payload.rows, range: action.payload.range };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'RESET':
		return { rows: [], loading: false, error: '', range: null };
	default:
		return state;
	}
};

function defaultTwelveMonthRange() {
	const from = moment.utc().startOf('month');
	const to = from.clone().add(11, 'months').endOf('month');
	return { dateFrom: from.format('YYYY-MM-DD'), dateTo: to.format('YYYY-MM-DD') };
}

function buildDetailColumns(type) {
	const relKey = type.amountKey;
	return [
		{ key: 'date', header: 'Fecha', render: (row) => moment.utc(row.date).format('DD/MM/YYYY') },
		{ key: 'account', header: relKey === 'debt' ? 'Deudor' : 'Acreedor', render: (row) => row[relKey]?.customId ?? '—' },
		{ key: 'concept', header: 'Concepto', render: (row) => row[relKey]?.concept ?? '—' },
		{ key: 'customId', header: 'Cuota', render: (row) => row.customId ?? '—' },
		{
			key: 'amount',
			header: 'Monto',
			className: 'tw:text-right tw:font-mono',
			render: (row) => `${showCurrency(row[relKey]?.currency) || ''} ${Number(row.amount || 0).toLocaleString('es-UY')}`.trim(),
		},
		{ key: 'expiration', header: 'Vencimiento', render: (row) => (row.expiration ? moment.utc(row.expiration).format('DD/MM/YYYY') : '—') },
		{ key: 'paid', header: 'Pago', render: (row) => (row.paid ? 'Sí' : 'No') },
	];
}

function Predictions() {
	const [{ rows, loading, error, range }, dispatch] = useReducer(reducer, { rows: [], loading: false, error: '', range: null });
	const { search } = useLocation();
	const navigate = useNavigate();

	const sp = new URLSearchParams(search);
	const typeIdFromUrl = sp.get('type') || null;

	const [selectedId, setSelectedId] = useState(typeIdFromUrl);
	const [filters, setFilters] = useState(DEFAULT_FILTERS);
	const [dateErrors, setDateErrors] = useState({});
	const [genState, setGenState] = useState('idle');
	const [genResult, setGenResult] = useState(null);
	const [showDetail, setShowDetail] = useState(false);
	const [detailPage, setDetailPage] = useState(1);

	const type = getPredictionType(selectedId);

	const hydrated = useRef(false);
	useEffect(() => {
		if (hydrated.current) return;
		hydrated.current = true;
		if (typeIdFromUrl && getPredictionType(typeIdFromUrl)) {
			setSelectedId(typeIdFromUrl);
		}
	}, []);

	function handlePickType(id) {
		if (id === selectedId) return;
		setSelectedId(id);
		setGenState('idle');
		setGenResult(null);
		setShowDetail(false);
		setDetailPage(1);
		dispatch({ type: 'RESET' });
		navigate(`/AdminScreen/predictions?type=${encodeURIComponent(id)}`, { replace: true });
	}

	function handleFilterChange(patch) {
		setFilters((prev) => ({ ...prev, ...patch }));
		setDetailPage(1);
	}

	function handleClearFilter() {
		setFilters(DEFAULT_FILTERS);
		setDateErrors({});
		setDetailPage(1);
	}

	// Custom horizonte needs both dates before it's a valid range to fetch;
	// fixed horizontes (3/6/12) always fetch the same 12-month baseline and
	// slice client-side, so no refetch happens when switching among them.
	const customReady = filters.horizonte === 'custom' && filters.dateFrom && filters.dateTo;
	const customInvalid = filters.horizonte === 'custom' && filters.dateFrom && filters.dateTo && filters.dateTo < filters.dateFrom;

	useEffect(() => {
		if (!type) return;
		if (filters.horizonte === 'custom') {
			if (!customReady) {
				setDateErrors({
					dateFrom: filters.dateFrom ? '' : 'Seleccione una fecha de inicio',
					dateTo: filters.dateTo ? '' : 'Seleccione una fecha de fin',
				});
				return;
			}
			if (customInvalid) {
				setDateErrors({ dateTo: 'La fecha de fin debe ser posterior a la de inicio' });
				return;
			}
			setDateErrors({});
			fetchRange(type, filters.dateFrom, filters.dateTo);
		} else {
			const { dateFrom, dateTo } = defaultTwelveMonthRange();
			fetchRange(type, dateFrom, dateTo);
		}
	}, [type?.id, filters.horizonte === 'custom', filters.horizonte === 'custom' ? filters.dateFrom : null, filters.horizonte === 'custom' ? filters.dateTo : null]);

	async function fetchRange(currentType, dateFrom, dateTo) {
		dispatch({ type: 'FETCH_REQUEST' });
		try {
			const params = new URLSearchParams({ dateFrom, dateTo, pageSize: 10000, page: 1 });
			const { data } = await currentType.service.listFuture(params.toString());
			dispatch({ type: 'FETCH_SUCCESS', payload: { rows: data[currentType.allKey] || [], range: { dateFrom, dateTo } } });
		} catch (err) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
			toast.error(getError(err));
		}
	}

	useEffect(() => {
		setDetailPage(1);
	}, [range]);

	const monthCount = useMemo(() => {
		if (!range) return 0;
		if (filters.horizonte === 'custom') {
			return moment.utc(range.dateTo).diff(moment.utc(range.dateFrom), 'months') + 1;
		}
		return Math.min(Number(filters.horizonte), moment.utc(range.dateTo).diff(moment.utc(range.dateFrom), 'months') + 1);
	}, [range, filters.horizonte]);

	const months = useMemo(() => {
		if (!type || !range || monthCount === 0) return [];
		return buildProjection(rows, { monthsFrom: range.dateFrom, monthCount, amountKey: type.amountKey });
	}, [rows, range, monthCount, type]);

	const horizonteLabel = filters.horizonte === 'custom'
		? `${moment.utc(range?.dateFrom).format('MMM YY')} – ${moment.utc(range?.dateTo).format('MMM YY')}`
		: `Próximos ${filters.horizonte} meses`;

	// Detail rows: only the months currently in view, filtered by the
	// selected currency — mirrors what the summary/table above show.
	const detailRows = useMemo(() => {
		if (!type || months.length === 0) return [];
		const mesSet = new Set(months.map((m) => m.mes));
		return rows.filter((row) => {
			if (!mesSet.has(moment.utc(row.date).format('YYYY-MM'))) return false;
			if (filters.moneda === 'all') return true;
			return row[type.amountKey]?.currency === filters.moneda;
		});
	}, [rows, months, type, filters.moneda]);

	const pageStart = (detailPage - 1) * DETAIL_PAGE_SIZE;
	const pagedDetailRows = detailRows.slice(pageStart, pageStart + DETAIL_PAGE_SIZE);
	const detailColumns = useMemo(() => (type ? buildDetailColumns(type) : []), [type]);

	async function handleGenerate() {
		if (!type || months.length === 0) return;
		setGenState('preparing');
		try {
			const result = generatePredictionFile(type, detailRows, months);
			setGenResult(result);
			setGenState('ready');
		} catch (err) {
			toast.error(getError(err));
			setGenState('idle');
		}
	}

	function handleDownload() {
		if (genResult) saveBlob(genResult.blob, genResult.filename);
	}

	return (
		<EntityList>
			<PageHead
				breadcrumb={[{ label: 'Inicio', to: '/AdminScreen' }, { label: 'Previsiones' }]}
				title={
					<span className='tw:inline-flex tw:items-center tw:gap-3'>
						<span className='tw:grid tw:h-[38px] tw:w-[38px] tw:place-items-center tw:rounded-[10px] tw:bg-accent-soft tw:text-accent-ink'>
							<MdAddchart size={20} />
						</span>
						Generación de previsiones
					</span>
				}
				subtitle='Elegí el tipo de previsión, ajustá los filtros y proyectá el flujo de los próximos meses.'
			/>

			<div className='tw:grid tw:grid-cols-1 tw:items-start tw:gap-6 tw:min-[1080px]:grid-cols-[1fr_360px]'>
				<div>
					<PredictionTypeCards selectedId={selectedId} onSelect={handlePickType} />
					<ProjectionPanel
						type={type}
						months={loading ? null : months}
						moneda={filters.moneda}
						horizonteLabel={horizonteLabel}
						showDetail={showDetail}
						onToggleDetail={() => setShowDetail((prev) => !prev)}
						detailRows={
							<div>
								<DataTable
									columns={detailColumns}
									rows={pagedDetailRows}
									loading={loading}
									error={error}
									rowKey={(row) => row._id}
									emptyMessage='No se encontraron cuotas para el rango y moneda seleccionados.'
								/>
								<div className='tw:mt-3'>
									<Paginator
										totalCount={detailRows.length}
										onPageChange={setDetailPage}
										currentPage={detailPage}
										pageSize={DETAIL_PAGE_SIZE}
									/>
								</div>
							</div>
						}
					/>
				</div>
				<div className='tw:flex tw:flex-col tw:gap-3 tw:min-[1080px]:sticky tw:min-[1080px]:top-6'>
					<PredictionFilterPanel
						type={type}
						filters={filters}
						dateErrors={dateErrors}
						onFilterChange={handleFilterChange}
						onClear={handleClearFilter}
						genState={genState}
						genResult={genResult}
						onGenerate={handleGenerate}
						onBack={() => setGenState('idle')}
						onDownload={handleDownload}
					/>
				</div>
			</div>
		</EntityList>
	);
}

export default Predictions;
